import {
  Heading,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalCloseButton,
  Box,
  Button,
  Input,
  Flex,
} from '@chakra-ui/react';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useResources } from '@/lib/datx-jsonapi-react';
import { queryProfilePlaylists } from '@/queries/profile-playlists';
import { useStores } from '@/hooks/useStores';
import useSession from '@/hooks/useSession';
import { ThinProfile } from '@/resources/ThinProfile';
import { queryThinProfilesByUserId } from '@/queries/thin-profiles';
import { IToastType } from '@/enums/ToastType';
import { ProfilePlaylist } from '@/resources/ProfilePlaylist';
import { useForm } from 'react-hook-form';
import { useToast } from '@/hooks/useToast';
import { PlaylistsList } from '@/components/ui/features/lecture/BookmarkLectureModal/components/PlaylistsList';

interface ICiteLectureModalProps {
  lectureId: string;
  isOpen: boolean;
  onClose: () => void;
}

export const BookmarkLectureModal: FC<ICiteLectureModalProps> = ({ lectureId, isOpen, onClose }) => {
  const { t } = useTranslation();

  const { store } = useStores();
  const { user } = useSession();
  const toast = useToast();

  const {
    register,
    handleSubmit,
    formState: { isSubmitting, isValid },
    reset,
  } = useForm({
    mode: 'onChange',
  });

  const { data } = useResources<ThinProfile>(queryThinProfilesByUserId(user?.id));

  const { mutate } = useResources(queryProfilePlaylists(data?.[0]?.id));

  const onSubmit = async (formData) => {
    try {
      await store.request(`${ThinProfile.type}/${data?.[0]?.id}/playlists`, 'POST', {
        data: {
          type: ProfilePlaylist.type,
          attributes: {
            name: formData.name,
          },
        },
      });

      reset();
      mutate();

      toast({ status: IToastType.SUCCESS, title: t('newLecture.bookmarkModal.successfulSubmit') });
    } catch (response) {
      toast({ status: IToastType.ERROR, title: response.error[0].detail });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered variant="inverted">
      <ModalOverlay />
      <ModalContent w={{ base: '100vw', md: '584px' }}>
        <ModalCloseButton />
        <ModalBody p="0" py="0" backgroundColor="#1c1d1d">
          <Flex h="full" flexDirection="column">
            <Box flexGrow="1" p="10">
              <Heading fontSize="3xl" lineHeight="10">
                {t('newLecture.bookmarkModal.heading')}
              </Heading>
              <PlaylistsList lectureId={lectureId} />
            </Box>
            <Box as="form" onSubmit={handleSubmit(onSubmit)} p="10" backgroundColor="gray.900">
              <Flex flexDirection="column">
                <Input
                  {...register('name', {
                    required: { value: true, message: t('newLecture.bookmarkModal.nameFieldRequired') },
                  })}
                  w="full"
                  borderRadius="0"
                  borderWidth="0 0 1px 0"
                  pl="0"
                  _focus={{ boxShadow: 'none', borderColor: 'violet.500' }}
                  placeholder={t('newLecture.bookmarkModal.playlistNamePlaceholder')}
                />
                <Button
                  textTransform="lowercase"
                  mt="6"
                  alignSelf="flex-end"
                  variant="solid"
                  size="sm"
                  type="submit"
                  isDisabled={!isValid || isSubmitting}
                  color="white"
                >
                  {t('newLecture.bookmarkModal.createPlaylistButtonLabel')}
                </Button>
              </Flex>
            </Box>
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
